import type { Location } from "@finn-no/mobility-dealer-types";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react";
import { GoogleMapsLink } from "@mobility-item-page/dealer/client";
import { ElementType } from "@mobility-item-page/utilities/client";
import { Expandable } from "@warp-ds/elements/react/expandable";
import { Alert, Button } from "@warp-ds/react";
import { useEffect, useState } from "react";
import { type GoogleMapsEmbed, useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { trackOpenCookieSettings, trackViewMap } from "../../utils/pulseHelper.ts";

const GoogleMapsSection = ({
  mapsEmbed,
  location,
  trackClick,
}: {
  mapsEmbed: GoogleMapsEmbed;
  location: Location;
  trackClick: (elementType: ElementType, linkUrl?: string) => void;
}) => {
  const { isMobile, locale, consent } = useAppConfig();
  const [hasConsent, setHasConsent] = useState<boolean>(consent.marketing);

  if (!location?.street || !location.mapInfo) {
    return <div></div>;
  }

  const shortAddress = `${location?.zipCode} ${location?.city ?? location?.country ?? ""}`;
  const fullAddress = `${location.street}, ${shortAddress}`;

  return (
    <div>
      <h3 className="mb-16">
        <Trans id="dealer.dealerOverview.visitUs" message="Visit us" />
      </h3>
      <GoogleMapsLink mapInfo={location.mapInfo} trackClick={(url) => trackClick(ElementType.Link, url)} />
      {hasConsent ? (
        <iframe
          className="border rounded-8 mt-16"
          loading="eager"
          title="Google Maps"
          width="100%"
          height={isMobile ? "170" : "232"}
          referrerPolicy="no-referrer-when-downgrade"
          src={`${mapsEmbed.apiUrl}?key=${mapsEmbed.apiKey}&zoom=10&language=${locale}&q=${encodeURIComponent(fullAddress)}`}
          allowFullScreen
        ></iframe>
      ) : (
        <MissingConsent setConsent={setHasConsent} />
      )}
    </div>
  );
};

const MissingConsent = ({ setConsent }: { setConsent: (value: boolean) => void }) => {
  const [hasTracked, setHasTracked] = useState(false);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleExpand = () => {
    if (!hasTracked) {
      trackViewMap();
      setHasTracked(true);
      if (getCmpMarketingCookieConsent()) {
        setConsent(true);
      }
    }
  };

  return (
    <Expandable
      title={i18n.t({ id: "dealer.dealerMap.showMap", message: "Show map" })}
      className="mt-16"
      onClick={toggleExpand}
    >
      <Alert type="info" show className="mt-16">
        <div className="flex flex-wrap gap-8">
          <Trans
            id="dealer.dealerMap.needConsentText"
            message="To view the map, you must first accept marketing cookies. You may need to reload the page afterwards."
          />
          {isClient && window._tcf_ && window._tcf_.showPrivacyManager && (
            <Button
              link
              onClick={() => {
                trackOpenCookieSettings();
                if (window._tcf_?.showPrivacyManager) {
                  window._tcf_.showPrivacyManager();
                }
              }}
            >
              <Trans id="dealer.dealerMap.openCookiesettings" message="Open cookiesettings" />
            </Button>
          )}
        </div>
      </Alert>
    </Expandable>
  );
};

const getCmpMarketingCookieConsent = (): boolean => {
  if (typeof document === "undefined") return false;

  const cookies = document.cookie.split("; ");
  const cmpCookie = cookies.find((cookie) => cookie.startsWith("_cmp_marketing="));
  const value = cmpCookie?.split("=")[1];

  return value === "1";
};

export default GoogleMapsSection;
